/* eslint-disable */
import { useEffect, useMemo, useState } from 'react';
import { SelectPicker } from 'rsuite';
import { PlusCircle, X } from 'lucide-react';
import { Button } from '@/Components/ui/button';
import { Input } from '@/Components/ui/input';
import { notificationServices } from '@/Services';
import cityServices from '@/Services/Analytics Services/city.services';

// City names are saved with only the first letter capitalised and the rest
// lower-cased, and must not contain numeric digits.
const formatCityName = (value = '') => {
	const cleaned = String(value).replace(/[0-9]/g, '').trim();
	return cleaned ? cleaned.charAt(0).toUpperCase() + cleaned.slice(1).toLowerCase() : '';
};

const CityMaster = () => {
	const [cities, setCities] = useState([]);
	const [states, setStates] = useState([]);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [search, setSearch] = useState('');
	const [showAdd, setShowAdd] = useState(false);
	const [selectedStateId, setSelectedStateId] = useState(null);
	const [cityName, setCityName] = useState('');

	const fetchCities = async () => {
		setLoading(true);
		try {
			const res = await cityServices.listCities();
			if (res?.statusCode === 200) {
				setCities(Array.isArray(res?.data) ? res.data : []);
			} else {
				notificationServices.generateNotification({
					type: 'error',
					message: res?.message || 'Failed to load cities.',
				});
			}
		} catch (error) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Failed to load cities.',
			});
		} finally {
			setLoading(false);
		}
	};

	const fetchStates = async () => {
		try {
			const res = await cityServices.listStates();
			if (res?.statusCode === 200) {
				setStates(Array.isArray(res?.data) ? res.data : []);
			}
		} catch (error) {
			// non-blocking; the state dropdown just stays empty
		}
	};

	useEffect(() => {
		fetchCities();
		fetchStates();
	}, []);

	const stateOptions = useMemo(
		() =>
			(states || []).map((s) => ({
				label: s?.stateName,
				value: s?.stateId,
			})),
		[states]
	);

	const filteredCities = useMemo(() => {
		const term = String(search || '').trim().toLowerCase();
		if (!term) return cities;
		return cities.filter(
			(c) =>
				String(c?.cityName || '').toLowerCase().includes(term) ||
				String(c?.stateName || '').toLowerCase().includes(term)
		);
	}, [cities, search]);

	const resetForm = () => {
		setSelectedStateId(null);
		setCityName('');
	};

	const closeAdd = () => {
		setShowAdd(false);
		resetForm();
	};

	const handleSave = async () => {
		if (!selectedStateId) {
			notificationServices.generateNotification({
				type: 'warning',
				message: 'Please select a state.',
			});
			return;
		}
		if (!cityName.trim()) {
			notificationServices.generateNotification({
				type: 'warning',
				message: 'Please enter a city name.',
			});
			return;
		}
		setSaving(true);
		try {
			const res = await cityServices.addCity({
				data: {
					name: formatCityName(cityName),
					stateId: selectedStateId,
				},
			});
			if (res?.statusCode === 200) {
				notificationServices.generateNotification({
					type: 'success',
					message: res?.message || 'City added successfully.',
				});
				closeAdd();
				fetchCities();
			} else {
				// Duplicate / validation errors come back with the backend message
				// (e.g. "City 'X' already exists for <State>").
				notificationServices.generateNotification({
					type: 'error',
					message: res?.message || 'Failed to add city.',
				});
			}
		} catch (error) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Failed to add city.',
			});
		} finally {
			setSaving(false);
		}
	};

	return (
		<div className='flex flex-col w-full h-full p-4 gap-3 text-left'>
			<div className='flex items-center justify-between gap-3 flex-wrap'>
				<span className='font-semibold text-lg'>City Master</span>
				<div className='flex items-center gap-2'>
					<Input
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						placeholder='Search city / state'
						className='w-[220px]'
					/>
					<Button
						className='flex items-center gap-1.5 primary-btn'
						onClick={() => setShowAdd(true)}
					>
						<PlusCircle size={16} />
						Add City
					</Button>
				</div>
			</div>

			<div className='flex-1 min-h-0 overflow-auto border border-gray-200 rounded-lg'>
				<table className='w-full border-collapse text-sm'>
					<thead className='sticky top-0 bg-gray-50 z-10'>
						<tr className='text-left text-gray-600'>
							<th className='px-4 py-2.5 font-semibold border-b border-gray-200 w-[80px] text-left'>S.No</th>
							<th className='px-4 py-2.5 font-semibold border-b border-gray-200 text-left'>City</th>
							<th className='px-4 py-2.5 font-semibold border-b border-gray-200 text-left'>State</th>
						</tr>
					</thead>
					<tbody>
						{loading ? (
							<tr>
								<td colSpan={3} className='px-4 py-8 text-center text-gray-400'>
									Loading…
								</td>
							</tr>
						) : filteredCities.length === 0 ? (
							<tr>
								<td colSpan={3} className='px-4 py-8 text-center text-gray-400'>
									No cities found
								</td>
							</tr>
						) : (
							filteredCities.map((c, idx) => (
								<tr key={c?.cityId ?? idx} className='hover:bg-gray-50'>
									<td className='px-4 py-2.5 border-b border-gray-100 text-gray-500'>{idx + 1}</td>
									<td className='px-4 py-2.5 border-b border-gray-100 font-medium'>{c?.cityName || '-'}</td>
									<td className='px-4 py-2.5 border-b border-gray-100'>{c?.stateName || '-'}</td>
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>

			{showAdd && (
				<div className='fixed inset-0 z-[1000] flex items-center justify-center bg-black/40'>
					<div className='w-[420px] max-w-[92vw] bg-white rounded-xl shadow-xl p-5'>
						<div className='flex items-center justify-between mb-4'>
							<span className='font-semibold text-base'>Add City</span>
							<button
								type='button'
								onClick={closeAdd}
								className='text-gray-400 hover:text-gray-600'
								aria-label='Close'
							>
								<X size={18} />
							</button>
						</div>
						<div className='flex flex-col gap-4'>
							<div className='flex flex-col gap-1.5'>
								<label className='text-[13px] font-medium text-gray-600'>
									State <span className='text-red-500'>*</span>
								</label>
								<SelectPicker
									data={stateOptions}
									value={selectedStateId}
									onChange={setSelectedStateId}
									placeholder='Select State'
									className='w-full'
									cleanable={false}
									searchable
									menuClassName='z-[1100]'
								/>
							</div>
							<div className='flex flex-col gap-1.5'>
								<label className='text-[13px] font-medium text-gray-600'>
									City <span className='text-red-500'>*</span>
								</label>
								<Input
									value={cityName}
									onChange={(e) => setCityName(e.target.value.replace(/[0-9]/g, ''))}
									placeholder='Enter city name'
									className='w-full'
								/>
							</div>
						</div>
						<div className='flex items-center justify-end gap-2 mt-5'>
							<Button variant='outline' onClick={closeAdd} disabled={saving}>
								Cancel
							</Button>
							<Button className='primary-btn' onClick={handleSave} disabled={saving}>
								{saving ? 'Saving…' : 'Save'}
							</Button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default CityMaster;
